import React, { useState } from 'react'

import {
  Box,
  DateInput,
  FormLabel,
  Grid,
  Input,
  RoundedPlainTextButtonMenu,
  TimeInput,
  makeAppStyles,
  RoundedPlainTextButton,
  CircularProgress,
  Dialog,
  PerceptAppBar,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
} from '@percept/mui'

import { ArrowDropDown, Check, Close } from '@percept/mui/icons'

import { Formik, FormikErrors } from 'formik'

import { NavigationTabs } from 'components/NavigationTabs'

import { ImageUpload } from 'components/ImageUpload'

import { TimezoneSelect } from './TimezoneSelect'

import {
  CreateMediaAcademyWebinarParams,
  EditMediaAcademyWebinarParams,
  useAddMediaAcademyWebinar,
  useEditMediaAcademyWebinar,
} from '../hooks'

import { capitalize } from 'lodash-es'

import { utcToZonedTime } from 'date-fns-tz'

import { Nullable } from '@percept/types'

import { MediaAcademyWebinar } from '../typings'

import { AdminDialogCloseHandler } from './typings'


type WebinarFormState = Nullable<CreateMediaAcademyWebinarParams>

const initialValues: WebinarFormState = {
  title: null,
  summary: null,
  start: null,
  timezone: 'Europe/London',
  category: null,
  additional_url: null,
  webinar_url: null,
  image_url: null,
  image_upload: null,
}


const useStyles = makeAppStyles( theme => ({
  inputContainer: {
    maxWidth: '40rem',
  },
  formLabel: {
    display: 'block',
    marginBottom: theme.spacing(1),
  },
  imgPreview: {
    maxHeight: '10rem',
    maxWidth: '20rem',
    margin: theme.spacing(4, 0),
  },
}))

export type ManageWebinarProps = {
  webinar?: MediaAcademyWebinar | null
  onClose: AdminDialogCloseHandler
  onSuccess?: () => void
}

export const ManageWebinar = ({
  webinar,
  onSuccess,
  onClose,
}: ManageWebinarProps): JSX.Element => {
  const useWebinarManagementHook = (
    webinar ?
      useEditMediaAcademyWebinar :
      useAddMediaAcademyWebinar
  )

  const query = useWebinarManagementHook({
    onSuccess,
  })

  const classes = useStyles()

  const [imageType, setImageType] = useState<'UPLOAD' | 'EXISTING'>('EXISTING')

  const actionText = `${webinar ? 'Edit' : 'Add'} Webinar`

  return (
    <Formik
      initialValues={
        webinar  ? {
          ...webinar,
          start: utcToZonedTime(new Date(webinar.start_datetime), webinar.timezone),
        } : initialValues
      }
      validate={(values): FormikErrors<WebinarFormState> => {
        const errors: FormikErrors<WebinarFormState> = {}
        const requiredFields: (keyof WebinarFormState)[] = [
          'category',
          'start',
          'timezone',
          'title',
        ]
        for( const field of requiredFields ){
          if( !values[field] ){
            errors[field] = `${field} is required`
          }
        }
        return errors
      }}
      onSubmit={(values): void => {
        query.mutate(values as EditMediaAcademyWebinarParams)
      }}>
      { ({ values, handleBlur, handleChange, setFieldValue, handleSubmit, isValid, dirty }): JSX.Element => (
        <form
          noValidate
          onSubmit={handleSubmit}>
          <Dialog
            open
            onClose={(): void => {
              onClose({ hasUnsavedChanges: dirty })
            }}
            fullWidth
            scroll='paper'
            maxWidth='lg'>
            <PerceptAppBar
              color='primary'
              position='relative'>
              <Box display='flex' alignItems='center'>
                <Box mr={3}>
                  <IconButton
                    color='inherit'
                    onClick={(): void => {
                      onClose({ hasUnsavedChanges: dirty })
                    }}>
                    <Close />
                  </IconButton>
                </Box>
                <Typography variant='h4'>{actionText}</Typography>
              </Box>
            </PerceptAppBar>
            <DialogContent>
              <Grid container spacing={5}>
                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>Title</FormLabel>
                    <Input
                      fullWidth
                      autoFocus
                      name='title'
                      value={values.title}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>Webinar URL</FormLabel>
                    <Input
                      fullWidth
                      name='webinar_url'
                      value={values.webinar_url}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>Summary</FormLabel>
                    <Input
                      fullWidth
                      multiline
                      name='summary'
                      value={values.summary}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div className={classes.inputContainer}>
                    <FormLabel className={classes.formLabel}>Presentation Link</FormLabel>
                    <Input
                      fullWidth
                      name='additional_url'
                      value={values.additional_url}
                      onBlur={handleBlur}
                      onChange={handleChange} />
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <FormLabel className={classes.formLabel}>Category</FormLabel>
                  <RoundedPlainTextButtonMenu
                    TriggerProps={{
                      variant: 'contained',
                      color: 'secondary',
                      endIcon: <ArrowDropDown />,
                    }}
                    value={values.category}
                    label={!values.category ? 'Select Category' : capitalize(values.category)}
                    options={
                      ['ENABLE', 'EXPAND', 'EXTRACT', 'MARTECH'].map( value => ({
                        value,
                        label: capitalize(value),
                      }))
                    }
                    onChange={(e, value): void => {
                      setFieldValue('category', value)
                    }} />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel className={classes.formLabel}>Host Timezone</FormLabel>
                  <TimezoneSelect
                    value={values.timezone}
                    onChange={(value): void => {
                      setFieldValue('timezone', value)
                    }} />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel className={classes.formLabel}>Date & Time</FormLabel>
                  <Box display='flex'>
                    <DateInput
                      value={values.start}
                      color='secondary'
                      variant='static'
                      format='dd/MM/yy'
                      onChange={(date): void => {
                        setFieldValue('start', date)
                      }} />
                    <TimeInput
                      value={values.start}
                      color='secondary'
                      variant='static'
                      format='HH:mm'
                      ampm={false}
                      onChange={(date): void => {
                        setFieldValue('start', date)
                      }} />
                  </Box>
                </Grid>

                {/* <Input
              fullWidth
              name='image_url'
              value={
                values.image_url && isS3ImageUrl(values.image_url) ?
                  '' :  
                  values.image_url
              }
              onBlur={handleBlur}
              onChange={handleChange} /> */}

                { !webinar && (
                  <Grid item xs={8}>
                    <FormLabel className={classes.formLabel}>Upload Image</FormLabel>
                    <ImageUpload
                      file={values.image_upload}
                      setFile={(file): void => {
                        setFieldValue('image_upload', file)
                      }} />
                  </Grid>
                )}

                { !!webinar && (
                  <Grid item xs={8}>
                    <FormLabel className={classes.formLabel}>Image</FormLabel>
                    <NavigationTabs
                      value={imageType}
                      options={[
                        {value: 'EXISTING', label: 'Existing Image'},
                        {value: 'UPLOAD', label: 'Upload New'},
                      ]}
                      onChange={setImageType} />
                    <Box display='flex' justifyContent='center'>
                      { imageType === 'EXISTING' && !webinar.image_url && (
                        <Box fontSize={16} p={6} color='text.secondary'>
                      No image associated with this webinar
                        </Box>
                      )}
                      { imageType === 'EXISTING' && webinar.image_url && (
                        <img
                          className={classes.imgPreview}
                          src={webinar.image_url} />
                      )}
                      { imageType === 'UPLOAD' && (
                        <ImageUpload
                          file={values.image_upload}
                          setFile={(file): void => {
                            setFieldValue('image_upload', file)
                          }} />
                      )}
                    </Box>
                  </Grid>
                ) }

              </Grid>
            </DialogContent>

            <DialogActions>
              <Box pb={0.5} display='flex' alignItems='center'>
                <Box mr={2}>
                  <RoundedPlainTextButton
                    size='large'
                    variant='contained'
                    startIcon={<Close />}
                    onClick={(): void => {
                      onClose({
                        hasUnsavedChanges: dirty
                      })
                    }}>
                    Cancel
                  </RoundedPlainTextButton>
                </Box>
                <RoundedPlainTextButton
                  type='submit'
                  size='large'
                  variant='contained'
                  onClick={handleSubmit as unknown as React.MouseEventHandler}
                  color='primary'
                  startIcon={
                    query.isLoading ?
                      <CircularProgress size='1em' color='inherit' /> :
                      <Check />
                  }
                  disabled={!isValid || query.isLoading || !dirty}>
                  {actionText}
                </RoundedPlainTextButton>
              </Box>
            </DialogActions>
          </Dialog>
        </form>
      )}
    </Formik>
  )
}
